import React, { useCallback, useEffect, useState } from 'react';
import { Power, AlertTriangle, CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase } from '../../api/httpClient';
import { toast } from 'sonner';

/**
 * iter295 — Phase 3 Admin Dashboard.
 *
 * Single setting today: payments kill-switch.
 * When enabled, the operator can flip payments to "disabled" with a
 * mandatory reason. The change is audit-logged. The frontend payment
 * flows must read this flag (separate work) — this page provides the
 * canonical UI so operators can act in seconds.
 */

export default function AdminSettings() {
  const { token } = useAuth();
  const isArabic = (document.documentElement.lang || 'en').startsWith('ar');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pendingReason, setPendingReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${getApiBase()}/api/admin/settings/payments`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!r.ok) {
        let msg = '';
        try { msg = (await r.json()).detail || ''; } catch (_e) {}
        setError({ status: r.status, message: msg || r.statusText });
        return;
      }
      const json = await r.json();
      setSettings(json);
    } catch (e) {
      setError({ status: 0, message: (e && e.message) || String(e) });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (nextDisabled) => {
    if (nextDisabled && !pendingReason.trim()) {
      toast.error(isArabic ? 'يرجى إدخال سبب الإيقاف' : 'Please enter a reason before disabling');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${getApiBase()}/api/admin/settings/payments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ disabled: nextDisabled, reason: nextDisabled ? pendingReason.trim() : '' }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(j.detail || (isArabic ? 'فشل التبديل' : 'Toggle failed'));
        return;
      }
      setSettings(j.settings);
      setPendingReason('');
      toast.success(
        nextDisabled
          ? (isArabic ? 'تم إيقاف المدفوعات' : 'Payments disabled')
          : (isArabic ? 'تم تفعيل المدفوعات' : 'Payments re-enabled')
      );
    } catch (e) {
      toast.error(isArabic ? 'حدث خطأ' : 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-settings" style={{ padding: 32, textAlign: 'center' }}>
        <Loader2 className="animate-spin" size={18} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="admin-settings-error" className="admin-card" style={{ borderInlineStart: '3px solid #ef4444', background: 'rgba(239,68,68,0.06)', margin: 16 }}>
        <h4 style={{ color: '#ef4444', margin: 0 }}>{isArabic ? 'تعذر تحميل الإعدادات' : 'Failed to load settings'}</h4>
        <pre style={{ marginTop: 8, fontSize: 11 }}>{JSON.stringify(error, null, 2)}</pre>
      </div>
    );
  }

  const disabled = Boolean(settings?.payments_disabled);

  return (
    <div data-testid="admin-settings">
      <div className="admin-ads-header">
        <div>
          <h1 className="admin-ads-title" data-testid="admin-settings-title">
            <ShieldAlert size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
            {isArabic ? 'إعدادات النظام' : 'System Settings'}
          </h1>
          <p className="admin-ads-subtitle">
            {isArabic
              ? 'مفاتيح حرجة تؤثر على تجربة كل المستخدمين. أي تغيير يُسجَّل في سجل المراجعة.'
              : 'Critical platform toggles. Every change is recorded in the audit log.'}
          </p>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 16, marginTop: 12 }} data-testid="admin-settings-payments-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Power size={18} color={disabled ? '#ef4444' : '#16a34a'} />
          <h2 style={{ margin: 0, fontSize: 16 }}>
            {isArabic ? 'مفتاح إيقاف المدفوعات' : 'Payments Kill-Switch'}
          </h2>
          <span
            className={`admin-pill ${disabled ? 'red' : 'green'}`}
            data-testid="admin-settings-payments-status"
            style={{ marginInlineStart: 'auto' }}
          >
            {disabled
              ? (isArabic ? 'مُعطَّل' : 'Disabled')
              : (isArabic ? 'مفعَّل' : 'Enabled')}
          </span>
        </div>

        <p style={{ fontSize: 13, color: 'var(--admin-text-dim, #475569)', lineHeight: 1.5 }}>
          {isArabic
            ? 'عند الإيقاف: تُعطَّل عمليات الدفع اليدوي (بانكك)، شراء العملات، وفتح ميزات Pro بالدفع. الميزات المجانية تستمر بشكل طبيعي. استخدم هذا المفتاح فقط إذا لاحظت مشكلة جدية في تدفق المدفوعات.'
            : 'When disabled: manual Bankak verification, coin purchases, and paid Pro unlocks are blocked. Free features continue normally. Use this kill-switch only when you spot a serious problem in the payment flow.'}
        </p>

        {disabled && settings?.disabled_reason && (
          <div
            className="admin-card"
            style={{ marginTop: 10, background: 'rgba(239,68,68,0.06)', borderInlineStart: '3px solid #ef4444', padding: 10 }}
            data-testid="admin-settings-payments-reason"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
              <AlertTriangle size={14} />
              {isArabic ? 'السبب الحالي:' : 'Current reason:'}
            </div>
            <div style={{ marginTop: 4, fontSize: 13 }}>{settings.disabled_reason}</div>
            {settings.disabled_at && (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--admin-text-dim, #64748b)' }}>
                {isArabic ? 'منذ' : 'Since'} {new Date(settings.disabled_at).toLocaleString()}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          {disabled ? (
            <button
              type="button"
              className="admin-btn"
              onClick={() => toggle(false)}
              disabled={saving}
              data-testid="admin-settings-payments-enable"
              style={{ background: '#16a34a', color: '#fff', fontWeight: 700 }}
            >
              {saving
                ? <><Loader2 className="animate-spin" size={14} /> {isArabic ? 'جارٍ التفعيل…' : 'Enabling…'}</>
                : <><CheckCircle2 size={14} /> {isArabic ? 'إعادة تفعيل المدفوعات' : 'Re-enable payments'}</>
              }
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }} htmlFor="payments-disable-reason">
                {isArabic ? 'سبب الإيقاف (مطلوب — يُسجَّل في سجل المراجعة):' : 'Reason for disabling (required — saved to audit log):'}
              </label>
              <input
                id="payments-disable-reason"
                type="text"
                value={pendingReason}
                onChange={(e) => setPendingReason(e.target.value)}
                maxLength={500}
                placeholder={isArabic ? 'مثلاً: مشكلة في بوابة بانكك' : 'e.g. Bankak gateway issue'}
                data-testid="admin-settings-payments-reason-input"
                className="admin-input"
              />
              <button
                type="button"
                className="admin-btn danger"
                onClick={() => toggle(true)}
                disabled={saving || !pendingReason.trim()}
                data-testid="admin-settings-payments-disable"
              >
                {saving
                  ? <><Loader2 className="animate-spin" size={14} /> {isArabic ? 'جارٍ الإيقاف…' : 'Disabling…'}</>
                  : <><Power size={14} /> {isArabic ? 'إيقاف المدفوعات الآن' : 'Disable payments now'}</>
                }
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
